import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from "@mui/material";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { PRAMI_COLORS } from "../expedicionCertificados/certificadosTheme";
import {
  COMPONENT_LABELS,
  formatCreditOldId,
  formatMoney,
  formatNumericInput,
  getHierarchyBlockers,
  hierarchyBlockersLabel,
  isQuotaOverdue,
  parseNumericInput,
  roundMoney,
  squareFieldSx,
} from "./condonationFormat";

// Componentes que suman el `balanceCredit` (saldo a pagar), calculados en caliente por el backend
// (`GET /v1/credits/{uuid}/payoff-breakdown`, misma fuente que `calculateCreditPayoff`).
const PAYOFF_COMPONENT_FIELDS = [
  { component: "CAPITAL", key: "capital" },
  { component: "INTERES_CORRIENTE", key: "currentInterest" },
  { component: "MORA", key: "mora" },
  { component: "GAC", key: "gac" },
  { component: "FGA", key: "fga" },
  { component: "SEGURO_VIDA", key: "lifeInsurance" },
  { component: "SEGURO_DESEMPLEO", key: "unemploymentInsurance" },
];

// Componentes condonables por el flujo "Por componentes": mapean el enum del backend con la clave del
// pendiente por cuota (`pending*` de /condonations/credits/{uuid}/quotas) y el prefijo del request
// (`{key}Percent`/`{key}Amount` de CreditComponentOfferRequest).
const CONDONABLE_COMPONENTS = [
  { component: "GAC", pendingKey: "pendingGac", requestKey: "gac" },
  { component: "MORA", pendingKey: "pendingMora", requestKey: "mora" },
  { component: "INTERES_CORRIENTE", pendingKey: "pendingInterest", requestKey: "interest" },
  { component: "FGA", pendingKey: "pendingFga", requestKey: "fga" },
  { component: "SEGURO_VIDA", pendingKey: "pendingLifeInsurance", requestKey: "lifeInsurance" },
  { component: "SEGURO_DESEMPLEO", pendingKey: "pendingUnemploymentInsurance", requestKey: "unemploymentInsurance" },
  { component: "CAPITAL", pendingKey: "pendingCapital", requestKey: "capital" },
];
const COMPONENT_META = CONDONABLE_COMPONENTS.reduce((acc, item) => {
  acc[item.component] = item;
  return acc;
}, {});

// Prefijo que identifica que la condonación se creó desde "Condonación sobre saldo total".
const SALDO_TOTAL_REASON_PREFIX = "[Condonación sobre saldo total]";

const ButtonAdd = styled(Button)({
  borderRadius: "20px !important",
  backgroundColor: "#161e55 !important",
  color: "#ffff !important",
  "&:hover": { backgroundColor: "#A8C8AF !important" },
});

/**
 * Campo de valor con selector porcentaje/monto (%/$) reutilizable.
 */
function AmountPercentField({ label, mode, value, onModeChange, onValueChange, helperText, disabled }) {
  return (
    <TextField
      fullWidth
      disabled={disabled}
      type="text"
      inputProps={{ inputMode: "decimal" }}
      label={label}
      value={value}
      onChange={(event) => onValueChange(formatNumericInput(event.target.value))}
      helperText={helperText}
      InputProps={{
        endAdornment: (
          <ToggleButtonGroup
            size="small"
            exclusive
            disabled={disabled}
            value={mode}
            onChange={(event, newMode) => {
              if (!newMode) {
                return;
              }
              onModeChange(newMode);
            }}
          >
            <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>
              %
            </ToggleButton>
            <ToggleButton value="amount" sx={{ px: 1, py: 0.25 }}>
              $
            </ToggleButton>
          </ToggleButtonGroup>
        ),
      }}
    />
  );
}

/**
 * "Condonar total del crédito". Dos flujos:
 *  - General: condona un % del saldo (`balanceCredit`) o un monto, repartido en cascada por jerarquía de
 *    cuotas sobre todos los componentes. POST /v1/condonations/credits/{uuid}/offers.
 *  - Mora/GAC: condona un monto (o %) del total de mora y/o del total de GAC del crédito, repartido sobre
 *    las cuotas (más antigua primero). POST /v1/condonations/credits/{uuid}/component-offers.
 */
export default function OfertaCreditoDialog({ open, credit, onClose, onSuccess }) {
  const [mode, setMode] = useState("general");
  // General
  const [generalMode, setGeneralMode] = useState("percent");
  const [generalValue, setGeneralValue] = useState("");
  // Por componentes: entrada (modo/valor) y checkbox "condonar todo" por componente, más el total
  // pendiente por componente y la config (habilitados + prioridad para la jerarquía).
  const [componentInputs, setComponentInputs] = useState({}); // { GAC: { mode, value }, ... }
  const [componentAll, setComponentAll] = useState({}); // { GAC: true, ... }
  const [componentTotals, setComponentTotals] = useState({}); // { GAC: pendiente, ... }
  const [componentConfig, setComponentConfig] = useState([]); // [{ component, enabled, priority }]
  // Desglose del saldo a pagar por componente (capital, interés, mora, GAC, FGA, seguros).
  const [payoffBreakdown, setPayoffBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [breakdownError, setBreakdownError] = useState("");
  // Uuid del crédito cuyo desglose ya terminó de cargar (éxito o error). Sirve para mostrar
  // la animación en el primer render, antes de que el efecto dispare el request.
  const [loadedBreakdownUuid, setLoadedBreakdownUuid] = useState(null);
  // Comunes
  const [expirationDate, setExpirationDate] = useState("");
  const [reason, setReason] = useState("");
  // Siempre se congela GAC y mora mientras la oferta esté vigente (el control ya no se muestra).
  const freezeGacAndMora = true;
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPayoffBreakdown(null);
      setBreakdownError("");
      setLoadedBreakdownUuid(null);
      setLoadingBreakdown(false);
      return;
    }
    setMode("general");
    setGeneralMode("percent");
    setGeneralValue("");
    setComponentInputs({});
    setComponentAll({});
    setExpirationDate("");
    setReason("");
    setSubmitting(false);
    setComponentTotals({});
    setPayoffBreakdown(null);
    setBreakdownError("");
    setLoadedBreakdownUuid(null);
    setLoadingBreakdown(false);
  }, [open, credit]);

  useEffect(() => {
    if (!open || !credit?.uuid) {
      return;
    }
    let active = true;
    axiosClient
      .get(`/v1/condonations/credits/${credit.uuid}/quotas`)
      .then((response) => {
        if (!active) {
          return;
        }
        const quotas = response.data || [];
        // Solo cuotas VENCIDAS (PENDING + payDate <= hoy) son condonables: los componentes de las cuotas
        // no vencidas no se pueden condonar. Espeja el filtro del backend (loadCondonableQuotasOrThrow).
        const overdueQuotas = quotas.filter((activeQuota) => isQuotaOverdue(activeQuota?.quota));
        // Total pendiente por componente = suma del pending de las cuotas vencidas (misma base que topa
        // el backend).
        const totals = CONDONABLE_COMPONENTS.reduce((acc, item) => {
          const sum = overdueQuotas.reduce(
            (total, quota) => total + Number(quota?.pending?.[item.pendingKey] || 0),
            0
          );
          acc[item.component] = roundMoney(sum);
          return acc;
        }, {});
        setComponentTotals(totals);
      })
      .catch(() => {
        if (active) {
          setComponentTotals({});
        }
      });
    return () => {
      active = false;
    };
  }, [open, credit]);

  // Config de componentes (habilitados + prioridad) para saber cuáles se pueden condonar y en qué orden
  // aplica la jerarquía.
  useEffect(() => {
    if (!open) {
      return;
    }
    let active = true;
    axiosClient
      .get(`/v1/condonations/config/components`)
      .then((response) => {
        if (active) {
          setComponentConfig(response.data || []);
        }
      })
      .catch(() => {
        if (active) {
          setComponentConfig([]);
        }
      });
    return () => {
      active = false;
    };
  }, [open]);

  // Trae el desglose del saldo a pagar por componente (calculado en caliente por el backend con la
  // misma lógica de `calculateCreditPayoff`, así la suma siempre cuadra con el balanceCredit).
  useEffect(() => {
    if (!open || !credit?.uuid) {
      return;
    }
    const creditUuid = credit.uuid;
    let active = true;
    setLoadingBreakdown(true);
    setBreakdownError("");
    axiosClient
      .get(`/v1/credits/${creditUuid}/payoff-breakdown`)
      .then((response) => {
        if (!active) {
          return;
        }
        setPayoffBreakdown(response.data || null);
        setLoadedBreakdownUuid(creditUuid);
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        const data = error.response?.data;
        const text = (value) => (typeof value === "string" && value.trim() ? value : "");
        const message =
          text(data) ||
          text(data?.description) ||
          text(data?.message) ||
          text(data?.error) ||
          "No se pudo cargar la composición del saldo.";
        setPayoffBreakdown(null);
        setBreakdownError(message);
        setLoadedBreakdownUuid(creditUuid);
      })
      .finally(() => {
        if (active) {
          setLoadingBreakdown(false);
        }
      });
    return () => {
      active = false;
    };
  }, [open, credit]);

  // El % de la oferta se dimensiona en el backend sobre el `balanceCredit` cacheado del crédito, así
  // que la base del preview usa ese mismo valor para que el estimado coincida con lo que hará el backend.
  const balanceCredit = Number(credit?.balanceCredit) || 0;

  // Desglose por componente del saldo a pagar (en caliente). Su suma (`total`) cuadra con el
  // `balanceCredit`; se muestra solo como referencia para ver de qué se compone el saldo.
  const breakdownComponents = payoffBreakdown
    ? PAYOFF_COMPONENT_FIELDS.map((field) => ({
        component: field.component,
        value: roundMoney(Math.max(0, Number(payoffBreakdown[field.key]) || 0)),
      }))
    : [];
  const breakdownTotal =
    payoffBreakdown?.total != null ? roundMoney(Number(payoffBreakdown.total) || 0) : null;

  const generalParsed = parseNumericInput(generalValue);
  const generalTarget =
    generalParsed == null
      ? 0
      : generalMode === "percent"
      ? roundMoney(balanceCredit * (generalParsed / 100))
      : roundMoney(generalParsed);

  // Componentes habilitados en orden de prioridad (capital siempre al final), intersectados con los que
  // tienen mapeo local. Es el orden en que aplica la jerarquía.
  const orderedComponents = componentConfig
    .filter((item) => item.enabled && COMPONENT_META[item.component])
    .sort((a, b) => {
      const aCapital = a.component === "CAPITAL" ? 1 : 0;
      const bCapital = b.component === "CAPITAL" ? 1 : 0;
      if (aCapital !== bCapital) {
        return aCapital - bCapital;
      }
      return (a.priority || 0) - (b.priority || 0);
    })
    .map((item) => item.component);

  const componentInputOf = (component) => componentInputs[component] || { mode: "amount", value: "" };

  // Monto que se condonaría de un componente: todo su total (checkbox) o el valor ingresado (monto o %),
  // topado a su total pendiente.
  const allocatedFor = (component) => {
    const total = componentTotals[component] || 0;
    if (componentAll[component]) {
      return roundMoney(total);
    }
    const input = componentInputOf(component);
    const parsed = parseNumericInput(input.value);
    if (parsed == null || parsed <= 0) {
      return 0;
    }
    const raw = input.mode === "percent" ? total * (parsed / 100) : parsed;
    return roundMoney(Math.min(Math.max(0, raw), total));
  };

  // Estructuras para la jerarquía (reutiliza helpers de condonationFormat): expected = total del
  // componente, y lo asignado por componente.
  const orderedForHierarchy = orderedComponents.map((component) => ({
    component,
    enabled: true,
    expected: componentTotals[component] || 0,
  }));
  const amountsByComponent = orderedComponents.reduce((acc, component) => {
    acc[component] = allocatedFor(component);
    return acc;
  }, {});
  const blockersFor = (component) =>
    getHierarchyBlockers(component, orderedForHierarchy, amountsByComponent);

  // Preview de lo que se condona y lo que le queda por pagar al cliente (estimado sobre el saldo del
  // crédito). El backend devuelve el valor exacto (`totalToPayAfter`) al crear la oferta.
  const componentsCondoned = orderedComponents.reduce(
    (sum, component) => sum + (blockersFor(component).length > 0 ? 0 : allocatedFor(component)),
    0
  );
  const condonedPreview =
    mode === "general" ? Math.min(generalTarget, balanceCredit) : componentsCondoned;
  const clientPaysAfter = roundMoney(Math.max(0, balanceCredit - condonedPreview));

  // Si un componente queda bloqueado por jerarquía (al bajar uno de mayor prioridad), su campo se
  // deshabilita; limpiamos su valor para que no quede atrapado ni cuente en el preview.
  useEffect(() => {
    const blockedSelected = orderedComponents.filter(
      (component) =>
        blockersFor(component).length > 0 &&
        (componentAll[component] || (componentInputs[component] && componentInputs[component].value))
    );
    if (blockedSelected.length === 0) {
      return;
    }
    setComponentAll((prev) => {
      const next = { ...prev };
      blockedSelected.forEach((component) => {
        delete next[component];
      });
      return next;
    });
    setComponentInputs((prev) => {
      const next = { ...prev };
      blockedSelected.forEach((component) => {
        if (next[component]) {
          next[component] = { mode: next[component].mode, value: "" };
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentInputs, componentAll, componentTotals, componentConfig]);

  // Verdadero desde el primer render del diálogo hasta que termina GET payoff-breakdown.
  const loadingComponents = Boolean(
    open && credit?.uuid && (loadingBreakdown || loadedBreakdownUuid !== credit.uuid)
  );

  if (!credit) {
    return null;
  }

  const validate = () => {
    if (!expirationDate) {
      warn("Falta la fecha", "Debes indicar la fecha de vencimiento de la oferta.");
      return false;
    }
    if (!reason.trim()) {
      warn("Falta el motivo", "Debes indicar el motivo de la oferta.");
      return false;
    }
    if (mode === "general") {
      if (generalParsed == null || generalParsed <= 0) {
        warn("Falta el valor", "Indica el porcentaje o el monto a condonar del crédito.");
        return false;
      }
      return true;
    }

    const selected = orderedComponents.filter((component) => allocatedFor(component) > 0);
    if (selected.length === 0) {
      warn("Falta el valor", "Selecciona al menos un componente y cuánto condonar.");
      return false;
    }
    // Jerarquía: un componente solo puede condonarse si los de mayor prioridad están condonados completos.
    for (const component of selected) {
      const blockers = blockersFor(component);
      if (blockers.length > 0) {
        warn(
          "Jerarquía de condonación",
          `Para condonar ${COMPONENT_LABELS[component] || component} primero debes condonar por completo: ${hierarchyBlockersLabel(
            blockers
          )}.`
        );
        return false;
      }
    }
    return true;
  };

  const warn = (title, text) =>
    Swal.fire({ icon: "warning", title, text, confirmButtonColor: "#161e55" });

  // Marca de origen: toda condonación creada desde "Condonación sobre saldo total" (general o por
  // componentes) lleva este prefijo al inicio de la razón para saber por qué flujo se generó.
  const commonRequest = () => ({
    expirationDate,
    freezeGacAndMora,
    reason: `${SALDO_TOTAL_REASON_PREFIX} ${reason.trim()}`.trim(),
    createdByUserUuid: localStorage.getItem("userUuid") || null,
  });

  const submit = () => {
    if (!validate()) {
      return;
    }
    const isGeneral = mode === "general";
    const url = isGeneral
      ? `/v1/condonations/credits/${credit.uuid}/offers`
      : `/v1/condonations/credits/${credit.uuid}/component-offers`;
    const body = commonRequest();
    if (isGeneral) {
      if (generalMode === "percent") {
        body.percent = generalParsed;
      } else {
        body.amount = generalParsed;
      }
    } else {
      // Por componente: "condonar todo" = 100% del componente; si no, el monto o % ingresado.
      orderedComponents.forEach((component) => {
        const meta = COMPONENT_META[component];
        if (!meta) {
          return;
        }
        const total = componentTotals[component] || 0;
        if (componentAll[component]) {
          if (total > 0) {
            body[`${meta.requestKey}Percent`] = 100;
          }
          return;
        }
        const input = componentInputOf(component);
        const parsed = parseNumericInput(input.value);
        if (parsed == null || parsed <= 0) {
          return;
        }
        if (input.mode === "percent") {
          body[`${meta.requestKey}Percent`] = parsed;
        } else {
          body[`${meta.requestKey}Amount`] = parsed;
        }
      });
    }

    setSubmitting(true);
    axiosClient
      .post(url, body)
      .then((response) => {
        setSubmitting(false);
        const data = response.data || {};
        const count = (data.offers || []).length;
        const scope = isGeneral ? "" : " por componentes";
        const afterText =
          data.totalToPayAfter != null
            ? ` El cliente debe pagar ${formatMoney(data.totalToPayAfter)} después de la condonación.`
            : "";
        onSuccess(
          `Se crearon ${count} oferta(s)${scope} por ${formatMoney(
            data.distributedAmount
          )} en el crédito ${formatCreditOldId(credit)}.${afterText}`
        );
        onClose();
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "No se pudo crear la oferta",
          text: error.response?.data?.message || "Revisa los datos ingresados.",
          confirmButtonColor: "#161e55",
        });
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
        {`Condonar total del crédito ${formatCreditOldId(credit)}`}
      </DialogTitle>
      <DialogContent
        sx={{
          ...squareFieldSx,
          "& .MuiTypography-h6": { fontSize: "1.05rem" },
          "& .MuiTypography-subtitle1": { fontSize: "0.9rem" },
          "& .MuiTypography-subtitle2": { fontSize: "0.8rem" },
          "& .MuiTypography-body2": { fontSize: "0.8rem" },
          "& .MuiTypography-caption": { fontSize: "0.7rem" },
          "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
          "& .MuiInputBase-input": { fontSize: "0.85rem" },
          "& .MuiFormHelperText-root": { fontSize: "0.7rem" },
        }}
      >
        {loadingComponents ? (
          <Box
            sx={{
              minHeight: 320,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={48} sx={{ color: PRAMI_COLORS.primary }} />
            <Typography variant="body2" color="text.secondary">
              Cargando composición del saldo…
            </Typography>
          </Box>
        ) : (
          <>
        <ToggleButtonGroup
          size="small"
          exclusive
          fullWidth
          value={mode}
          onChange={(event, newMode) => newMode && setMode(newMode)}
          sx={{ mt: 0.5, mb: 2 }}
        >
          <ToggleButton value="general">Condonar todo (general)</ToggleButton>
          <ToggleButton value="components">Por componentes</ToggleButton>
        </ToggleButtonGroup>

        {mode === "general" ? (
          <>
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: PRAMI_COLORS.pageBg,
                border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Saldo del crédito (balance)
              </Typography>
              <Typography variant="h6" sx={{ color: PRAMI_COLORS.primary, fontWeight: 700, lineHeight: 1.2 }}>
                {formatMoney(balanceCredit)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                El monto se reparte en cascada por jerarquía de cuotas (la más antigua primero) sobre todos
                los componentes, sin superar lo que realmente se debe.
              </Typography>

              {breakdownError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {breakdownError}
                </Alert>
              )}

              {breakdownComponents.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5, borderColor: PRAMI_COLORS.surfaceBorder }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Composición del saldo (cálculo en caliente)
                  </Typography>
                  <Grid container spacing={1}>
                    {breakdownComponents.map((item) => (
                      <Grid item xs={6} sm={4} key={item.component}>
                        <Typography variant="caption" color="text.secondary">
                          {COMPONENT_LABELS[item.component] || item.component}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: PRAMI_COLORS.primary }}>
                          {formatMoney(item.value)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                  {breakdownTotal != null && (
                    <Box
                      sx={{
                        mt: 1.5,
                        pt: 1,
                        borderTop: `1px dashed ${PRAMI_COLORS.surfaceBorder}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Suma de componentes
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRAMI_COLORS.primary }}>
                        {formatMoney(breakdownTotal)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>

            <AmountPercentField
              label={generalMode === "percent" ? "Porcentaje del saldo a condonar" : "Monto a condonar"}
              mode={generalMode}
              value={generalValue}
              onModeChange={(newMode) => {
                setGeneralMode(newMode);
                setGeneralValue("");
              }}
              onValueChange={setGeneralValue}
              helperText={`Se intentará condonar ${formatMoney(generalTarget)} del crédito`}
            />
          </>
        ) : (
          <>
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: PRAMI_COLORS.pageBg,
                border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Selecciona uno o varios componentes y cuánto condonar de cada uno (monto o %). Cada
                componente se reparte sobre las cuotas (la más antigua primero). Debe respetarse la
                jerarquía: un componente solo se puede condonar si los de mayor prioridad ya están
                condonados por completo.
              </Typography>
            </Box>

            {orderedComponents.length === 0 ? (
              <Alert severity="info">No hay componentes habilitados para condonar.</Alert>
            ) : (
              <Grid container spacing={2}>
                {orderedComponents.map((component) => {
                  const total = componentTotals[component] || 0;
                  const input = componentInputOf(component);
                  const blockers = blockersFor(component);
                  const blocked = blockers.length > 0;
                  const allChecked = Boolean(componentAll[component]);
                  const allocated = allocatedFor(component);
                  const noPending = total <= 0;
                  return (
                    <Grid item xs={12} sm={6} key={component}>
                      <FormControlLabel
                        sx={{ mb: 0.5 }}
                        control={
                          <Checkbox
                            checked={allChecked}
                            disabled={blocked || noPending}
                            onChange={(event) =>
                              setComponentAll((prev) => ({ ...prev, [component]: event.target.checked }))
                            }
                          />
                        }
                        label={`Condonar todo · ${COMPONENT_LABELS[component] || component}`}
                      />
                      <AmountPercentField
                        label={
                          input.mode === "percent"
                            ? `% de ${COMPONENT_LABELS[component] || component}`
                            : `Monto de ${COMPONENT_LABELS[component] || component}`
                        }
                        mode={input.mode}
                        value={input.value}
                        disabled={allChecked || blocked || noPending}
                        onModeChange={(newMode) =>
                          setComponentInputs((prev) => ({
                            ...prev,
                            [component]: { mode: newMode, value: "" },
                          }))
                        }
                        onValueChange={(newValue) =>
                          setComponentInputs((prev) => ({
                            ...prev,
                            [component]: { mode: input.mode, value: newValue },
                          }))
                        }
                        helperText={
                          blocked
                            ? `Primero condona por completo: ${hierarchyBlockersLabel(blockers)}`
                            : noPending
                            ? "Sin saldo pendiente de este componente"
                            : `Total ${formatMoney(total)} · condona ${formatMoney(allocated)}`
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: PRAMI_COLORS.pageBg,
            border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
            borderRadius: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Se condona
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {formatMoney(roundMoney(condonedPreview))}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" color="text.secondary">
              El cliente pagará después del descuento
            </Typography>
            <Typography variant="h6" sx={{ color: PRAMI_COLORS.primary, fontWeight: 700, lineHeight: 1.2 }}>
              {formatMoney(clientPaysAfter)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2, borderColor: PRAMI_COLORS.surfaceBorder }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Fecha de vencimiento"
              InputLabelProps={{ shrink: true }}
              value={expirationDate}
              onChange={(event) => setExpirationDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Motivo"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 2 }}>
          Se crea una oferta por cada cuota condonable; si alguna ya tiene una oferta activa, la operación
          se rechaza completa.
        </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <ButtonAdd disabled={submitting || loadingComponents} onClick={submit}>
          Crear oferta
        </ButtonAdd>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
